// @ts-ignore
import shallowequal from 'shallowequal';
import {
    BatchFunction, getBatch,
    SimpleStoreData,
    simpleTopicStoreTransformer,
    TopicStoreData,
    TransformerConfig,
    UseSimpleStore, UseTopicStore,
    useTopicStore,
} from '@om-fe/store';
import { MixedGroupedTournamentModel } from './mixed-grouped.types';
import { UpdateActionPayload } from '@om-fe/api';
import { TournamentModel } from '@om-fe/models';
import { useSportsTopicUrl } from '@om-fe/widgets/dist/common/topics/topic.hooks.ts'; 
import { useCallback, useEffect, useState } from 'react';
import create from 'zustand';

export type MixedGroupedStoreState = {
    readonly list: string[];
    readonly oddsList: string[];
    readonly outrightsList: string[];
    readonly oddsListNav: string[];
    readonly outrightsListNav: string[];
    readonly items: Record<string, MixedGroupedTournamentModel>;
};

export type UseMixedGroupedStore = UseTopicStore<MixedGroupedStoreState>;

const initialState: MixedGroupedStoreState = {
    list: [],
    oddsList: [],
    outrightsList: [],
    oddsListNav: [],
    outrightsListNav: [],
    items: {},
};

export const mixedGroupedModelStructure: Record<keyof MixedGroupedTournamentModel, keyof TournamentModel> = {
    id: 'id',
    name: 'name',
    translatedName: 'translatedName',
    shortTranslatedName: 'shortTranslatedName',
    locationId: 'venueId',
    locationName: 'venueName',
    sportId: 'sportId',
    sportName: 'sportName',
    categoryId: 'categoryId',
    categoryName: 'categoryName',
    displayChildren: 'displayChildren',
    shortSportName: 'shortSportName',
    shortLocationName: 'shortVenueName',
    numberOfLiveEvents: 'numberOfLiveEvents',
    numberOfUpcomingMatches: 'numberOfUpcomingMatches',
    numberOfOutrightMarkets: 'numberOfOutrightMarkets',
    parentId: 'parentId',
    endTime: 'endTime',
    numberOfOutrightMarketsForSubtournaments: 'numberOfOutrightMarketsForSubtournaments',
    isTopLevelTournament: 'isTopLevelTournament',
};

interface ParentTournamentStatuses {
    hasOddsChildren: boolean;
    hasOutrightsChildren: boolean;
}

function buildFromSimpleState(
    prevState: MixedGroupedStoreState,
    state: MixedGroupedStoreState,
): MixedGroupedStoreState {
    const { items, list } = state;

    const mutableParentTournamentStatuses: Record<string, ParentTournamentStatuses> = {};

    list.forEach((id: string) => {
        const item: MixedGroupedTournamentModel = items[id];

        if (!item.parentId) {
            return;
        }

        if (!mutableParentTournamentStatuses[item.parentId]) {
            mutableParentTournamentStatuses[item.parentId] = {
                hasOddsChildren: false,
                hasOutrightsChildren: false,
            };
        }

        const mutableParentStatus: ParentTournamentStatuses = mutableParentTournamentStatuses[item.parentId];

        if (item.numberOfLiveEvents || item.numberOfUpcomingMatches) {
            mutableParentStatus.hasOddsChildren = true;
        }
        if (item.numberOfOutrightMarkets) {
            mutableParentStatus.hasOutrightsChildren = true;
        }
    });

    let mutableOddsList: string[] = [];
    let mutableOutrightsList: string[] = [];
    let mutableOddsListNav: string[] = [];
    let mutableOutrightsListNav: string[] = [];

    list.forEach((id: string) => {
        const item: MixedGroupedTournamentModel = items[id];

        if (item.numberOfLiveEvents || item.numberOfUpcomingMatches) {
            mutableOddsListNav.push(item.id);

            if (!mutableParentTournamentStatuses[item.id]?.hasOddsChildren) {
                mutableOddsList.push(item.id);
            }
        }

        if (item.numberOfOutrightMarkets || item.numberOfOutrightMarketsForSubtournaments) {
            mutableOutrightsListNav.push(item.id);
            mutableOutrightsList.push(item.id);
        }
    });

    return {
        ...state,
        oddsList: shallowequal(prevState.oddsList, mutableOddsList)
            ? prevState.oddsList
            : mutableOddsList,
        outrightsList: shallowequal(prevState.outrightsList, mutableOutrightsList)
            ? prevState.outrightsList
            : mutableOutrightsList,
        oddsListNav: mutableOddsListNav,
        outrightsListNav: mutableOutrightsListNav,
    };
}

export function getTournamentsWithOddsList(
    list: string[],
    items: Record<string, MixedGroupedTournamentModel>,
): string[] {
    const mutableParentTournamentStatuses: Record<string, boolean | undefined> = {};

    list.forEach((id: string) => {
        const item: MixedGroupedTournamentModel = items[id];

        if (!item.parentId) {
            return;
        }

        if (item.numberOfLiveEvents || item.numberOfUpcomingMatches) {
            mutableParentTournamentStatuses[item.parentId] = true;
        }
    });

    return list.filter((id: string) => {
        const item: MixedGroupedTournamentModel = items[id];

        return (item.numberOfLiveEvents || item.numberOfUpcomingMatches) && !mutableParentTournamentStatuses[item.id];
    });
}

function transformer(
    payload: UpdateActionPayload<TournamentModel>,
    state: MixedGroupedStoreState,
): MixedGroupedStoreState {
    const newState: SimpleStoreData<MixedGroupedTournamentModel> = simpleTopicStoreTransformer(
        payload,
        state,
        mixedGroupedModelStructure,
    );

    return buildFromSimpleState(state, {
        ...newState,
        oddsList: state.oddsList,
        outrightsList: state.outrightsList,
        oddsListNav: state.oddsListNav,
        outrightsListNav: state.outrightsListNav,
    });
}

const transformerConfig: TransformerConfig = {};

export function useMixedGroupedTournamentsStoreCreator(topic: string): UseMixedGroupedStore {
    return useTopicStore<MixedGroupedStoreState, TournamentModel, TransformerConfig>(
        useSportsTopicUrl(topic),
        initialState,
        transformer,
        'mixedGroupedLocation',
        transformerConfig,
    );
}

export function useMixedGroupedFilteredStoreCreator(
    mixedStore: UseMixedGroupedStore,
    listKey: 'oddsList' | 'outrightsList' | 'oddsListNav' | 'outrightsListNav' | 'list',
): UseSimpleStore<MixedGroupedTournamentModel> {
    const buildState = useCallback((
        state: MixedGroupedStoreState & TopicStoreData,
    ): SimpleStoreData<MixedGroupedTournamentModel> & TopicStoreData => {
        const list: string [] = state[listKey];

        const items: Record<string, MixedGroupedTournamentModel> = list
            .reduce((
                mutableAcc: Record<string, MixedGroupedTournamentModel>,
                id: string,
            ): Record<string, MixedGroupedTournamentModel> => {
                mutableAcc[id] = state.items[id];

                return mutableAcc;
            }, {});

        return {
            ready: state.ready,
            list,
            items,
        };
    }, [listKey]);

    const [useStore] = useState(() => create<SimpleStoreData<MixedGroupedTournamentModel> & TopicStoreData>((): SimpleStoreData<MixedGroupedTournamentModel> & TopicStoreData => {
        return buildState(mixedStore.getState());
    }));

    useEffect(() => {
        const batch: BatchFunction = getBatch();

        return mixedStore.subscribe((state: MixedGroupedStoreState & TopicStoreData) => {
            batch(() => {
                useStore.setState(buildState(state));
            });
        });
    }, [buildState, mixedStore, useStore]);

    return useStore as UseSimpleStore<MixedGroupedTournamentModel>;
}
